import { createFileRoute, Link } from '@tanstack/react-router';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiDownload,
  FiLoader,
  FiMail,
  FiCheckCircle,
  FiFileText,
  FiUsers,
  FiUpload,
  FiArrowLeft,
  FiShield,
  FiInfo,
} from 'react-icons/fi';
import {
  useGetMailingList,
  useGetBroadcastEmails,
  useDeleteContact,
  useAddContacts,
  useUpdateContact,
  useCsvImport,
  useCsvExport,
  useRenameMailingList,
} from '../api/mailing-lists';
import { EventPoster } from '../components/mail/EventPoster';
import {
  EditContactModal,
  DeleteConfirmationModal,
  AddEmailsModal,
} from '../components/mail/Modals';
import type { MailingList, MailingContact, BroadcastEmail } from '../components/mail/types';
import { parseMailContactsCsvFile } from '../utils/mailCsv';
import { toast } from '../utils/toast';
import { isProfileSubscribersList } from '../utils/mail';

export const Route = createFileRoute('/mail/lists/$listUuid')({
  component: MailingListDetailPage,
});

type DetailTab = 'sent' | 'draft' | 'contacts';

function MailingListDetailPage() {
  const { listUuid } = Route.useParams();
  const [activeTab, setActiveTab] = useState<DetailTab>('sent');
  const [contactsSearch, setContactsSearch] = useState('');
  const [contactsPage, setContactsPage] = useState(1);
  const [broadcastStatus, setBroadcastStatus] = useState<'draft' | 'sent'>('sent');
  const [broadcastPage, setBroadcastPage] = useState(1);

  const [editContact, setEditContact] = useState<MailingContact | null>(null);
  const [deleteContact, setDeleteContactTarget] = useState<MailingContact | null>(null);
  const [showAddEmails, setShowAddEmails] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const deleteContactMutation = useDeleteContact();
  const addContactsMutation = useAddContacts(listUuid);
  const updateContactMutation = useUpdateContact();
  const csvImportMutation = useCsvImport();
  const csvExportMutation = useCsvExport();
  const renameListMutation = useRenameMailingList();

  const { data: listData, isLoading: listLoading } = useGetMailingList(listUuid, {
    page: contactsPage,
    page_size: 15,
    search: contactsSearch || undefined,
  });

  const { data: broadcastsData, isLoading: broadcastsLoading } = useGetBroadcastEmails(listUuid, {
    status: broadcastStatus,
    page: broadcastPage,
    page_size: 15,
  });

  const mailingList: MailingList | undefined = listData?.data?.mailing_list;
  const isSystemList = isProfileSubscribersList(mailingList);
  const contacts = listData?.data?.contacts?.results ?? [];
  const contactsCount = listData?.data?.contacts?.count ?? 0;
  const contactsNext = listData?.data?.contacts?.next;
  const contactsPrev = listData?.data?.contacts?.previous;

  const broadcasts: BroadcastEmail[] = broadcastsData?.results ?? [];
  const broadcastsCount = broadcastsData?.count ?? 0;

  // We'll fetch counts for the other tab in background via separate queries or rely on tab switching
  const tabSentCount = activeTab === 'sent' ? broadcastsCount : undefined;
  const tabDraftCount = activeTab === 'draft' ? broadcastsCount : undefined;

  const handleExportCsv = () => {
    if ((mailingList?.total_contacts ?? 0) === 0) {
      toast.warn('No contacts to export.');
      return;
    }
    csvExportMutation.mutate({
      listUuid,
      listName: mailingList?.name,
      filters: {
        search: contactsSearch || undefined,
        page: contactsPage,
        page_size: 15,
      },
    });
  };

  if (listLoading && !mailingList) {
    return (
      <div className="flex-1 flex items-center justify-center rounded-[16px] border border-[#262626] bg-[#0E0E0E]">
        <p className="text-sm text-[#737373]">Loading...</p>
      </div>
    );
  }

  if (!mailingList && !listLoading) {
    return (
      <div className="flex-1 flex items-center justify-center rounded-[16px] border border-[#262626] bg-[#0E0E0E]">
        <p className="text-sm text-[#737373]">List not found</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{mailingList?.name ?? 'List'} | Mail | Musicade</title>
      </Helmet>

      <div className="flex-1 flex flex-col rounded-none md:rounded-[16px] border-0 md:border border-[#262626] bg-[#0E0E0E] overflow-hidden min-w-0">
        {/* List header + stats bar */}
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-[#262626] flex-shrink-0">
          <div className="flex items-center gap-3 md:gap-4 mb-2 md:mb-3">
            <Link
              to="/mail"
              className="md:hidden p-1.5 rounded-lg hover:bg-white/5 text-[#A3A3A3] hover:text-white transition-colors flex-shrink-0"
            >
              <FiArrowLeft size={18} />
            </Link>
            {mailingList?.is_event_linked ? (
              <EventPoster name={mailingList.event_title || mailingList.name || ''} />
            ) : (
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-[#262626] flex items-center justify-center flex-shrink-0">
                <FiUsers size={16} className="md:hidden text-[#A3A3A3]" />
                <FiUsers size={18} className="hidden md:block text-[#A3A3A3]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              {showRename && !isSystemList ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && renameValue.trim()) {
                        renameListMutation.mutate(
                          { uuid: listUuid, payload: { name: renameValue.trim() } },
                          { onSuccess: () => setShowRename(false) },
                        );
                      }
                      if (e.key === 'Escape') setShowRename(false);
                    }}
                    className="text-lg font-semibold bg-transparent border-b border-[#333] text-white focus:outline-none focus:border-white"
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      if (renameValue.trim()) {
                        renameListMutation.mutate(
                          { uuid: listUuid, payload: { name: renameValue.trim() } },
                          { onSuccess: () => setShowRename(false) },
                        );
                      }
                    }}
                    className="text-xs text-[#2F80ED] hover:text-[#4A9AF0]"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setShowRename(false)}
                    className="text-xs text-[#737373] hover:text-white"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h2
                    className={`text-lg font-semibold text-white truncate ${!isSystemList ? 'cursor-pointer hover:text-gray-300 transition-colors' : ''}`}
                    onClick={() => {
                      if (!isSystemList) {
                        setRenameValue(mailingList?.name ?? '');
                        setShowRename(true);
                      }
                    }}
                    title={!isSystemList ? 'Click to rename' : undefined}
                  >
                    {mailingList?.name}
                  </h2>
                  {isSystemList ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-400 bg-blue-500/10 border border-blue-500/20 px-2.5 py-0.5 rounded-full flex-shrink-0">
                      <FiShield size={12} />
                      System List
                    </span>
                  ) : (
                    <button
                      onClick={() => {
                        setRenameValue(mailingList?.name ?? '');
                        setShowRename(true);
                      }}
                      className="p-1 rounded hover:bg-white/10 transition-colors flex-shrink-0"
                      title="Rename list"
                    >
                      <FiEdit2 size={12} className="text-[#737373]" />
                    </button>
                  )}
                </div>
              )}
              {mailingList?.is_event_linked && mailingList?.event_title && (
                <p className="text-xs text-[#737373]">Linked to event: {mailingList.event_title}</p>
              )}
            </div>
            <Link
              to="/mail/compose"
              search={{ listUuid: listUuid }}
              className="flex items-center gap-1 md:gap-1.5 px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-white text-black text-xs md:text-sm font-medium hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              <FiPlus size={12} className="md:hidden" />
              <FiPlus size={14} className="hidden md:block" />
              <span className="hidden sm:inline">Compose</span>
            </Link>
          </div>

          {/* Stats row */}
          <div className="flex flex-wrap items-center gap-2 md:gap-2.5">
            <StatItem value={mailingList?.total_contacts ?? 0} label="Total" />
            <StatItem value={mailingList?.active_contacts ?? 0} label="Active" variant="green" />
            <StatItem
              value={mailingList?.unsubscribed_count ?? 0}
              label="Unsubscribed"
              variant="red"
            />
          </div>

          {isSystemList && (
            <div className="mt-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs text-blue-300 flex items-center gap-2">
              <FiInfo size={16} className="flex-shrink-0 text-blue-400" />
              <span>
                System List: Contacts are managed automatically based on profile subscription
                actions.
              </span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-4 md:px-6 border-b border-[#262626] flex-shrink-0">
          <TabButton
            label="Sent"
            count={tabSentCount}
            isActive={activeTab === 'sent'}
            onClick={() => {
              setActiveTab('sent');
              setBroadcastStatus('sent');
              setBroadcastPage(1);
            }}
          />
          <TabButton
            label="Draft"
            count={tabDraftCount}
            isActive={activeTab === 'draft'}
            onClick={() => {
              setActiveTab('draft');
              setBroadcastStatus('draft');
              setBroadcastPage(1);
            }}
          />
          <TabButton
            label="Contacts"
            count={mailingList?.total_contacts}
            isActive={activeTab === 'contacts'}
            onClick={() => {
              setActiveTab('contacts');
              setContactsPage(1);
            }}
          />
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto">
          {/* Sent / Draft tabs */}
          {(activeTab === 'sent' || activeTab === 'draft') && (
            <div className="p-4 md:p-6">
              {broadcastsLoading && (
                <div className="text-center py-12 text-sm text-[#737373]">Loading...</div>
              )}

              {!broadcastsLoading && broadcasts.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-12 h-12 rounded-full bg-[#262626] flex items-center justify-center mb-4">
                    <FiMail size={20} className="text-[#A3A3A3]" />
                  </div>
                  <p className="text-white font-medium mb-1">
                    {activeTab === 'sent' ? 'No emails sent yet' : 'No drafts yet'}
                  </p>
                  <p className="text-sm text-[#737373] mb-4">
                    {activeTab === 'sent'
                      ? 'Emails you send to this list will appear here.'
                      : 'Saved drafts for this list will appear here.'}
                  </p>
                  <Link
                    to="/mail/compose"
                    search={{ listUuid: listUuid }}
                    className="flex items-center gap-2 px-5 py-2 rounded-full bg-white text-black text-sm font-medium hover:bg-gray-100 transition-colors"
                  >
                    <FiPlus size={14} />
                    Compose Email
                  </Link>
                </div>
              )}

              {!broadcastsLoading &&
                broadcasts.map((broadcast) => (
                  <Link
                    key={broadcast.uuid}
                    to="/mail/broadcasts/$broadcastUuid"
                    params={{ broadcastUuid: broadcast.uuid }}
                    search={{ listUuid }}
                    className="block p-3 md:p-4 rounded-lg md:rounded-xl border border-[#262626] hover:border-[#333] transition-colors mb-2"
                  >
                    <div className="flex items-center justify-between mb-1.5 md:mb-2">
                      <div className="flex items-center gap-2.5 md:gap-3 min-w-0">
                        <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-[#262626] flex items-center justify-center flex-shrink-0">
                          <FiMail size={13} className="md:hidden text-[#A3A3A3]" />
                          <FiMail size={14} className="hidden md:block text-[#A3A3A3]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs md:text-sm font-medium text-white truncate">
                            {broadcast.subject}
                          </p>
                          <p className="text-[10px] md:text-xs text-[#737373]">
                            {broadcast.sent_at
                              ? new Date(broadcast.sent_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : broadcast.scheduled_at
                                ? `Scheduled for ${new Date(broadcast.scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                                : new Date(broadcast.created_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  })}
                          </p>
                        </div>
                      </div>
                      <BroadcastStatusPill status={broadcast.status} />
                    </div>

                    {activeTab === 'sent' && (
                      <div className="flex items-center gap-4 md:gap-6 mt-2 md:mt-3 ml-10 md:ml-12">
                        <MiniStat value={broadcast.recipient_count} label="Recipients" />
                        <MiniStat value={broadcast.succeeded_count} label="Delivered" />
                        {broadcast.failed_count > 0 && (
                          <MiniStat value={broadcast.failed_count} label="Failed" variant="red" />
                        )}
                      </div>
                    )}
                  </Link>
                ))}

              {/* Pagination */}
              {broadcastsCount > 15 && (
                <div className="flex items-center justify-center gap-4 mt-4">
                  <button
                    disabled={!broadcastsData?.previous}
                    onClick={() => setBroadcastPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 text-xs text-[#A3A3A3] hover:text-white disabled:opacity-30 transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-[#737373]">Page {broadcastPage}</span>
                  <button
                    disabled={!broadcastsData?.next}
                    onClick={() => setBroadcastPage((p) => p + 1)}
                    className="px-3 py-1.5 text-xs text-[#A3A3A3] hover:text-white disabled:opacity-30 transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Contacts tab */}
          {activeTab === 'contacts' && (
            <div className="flex flex-col h-full">
              {/* Search + actions bar */}
              <div className="flex flex-col sm:flex-row gap-2 px-4 md:px-6 py-2.5 md:py-3 flex-shrink-0">
                <div className="flex items-center gap-2 w-full sm:flex-1">
                  <div className="relative flex-1">
                    <FiSearch
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]"
                    />
                    <input
                      type="text"
                      value={contactsSearch}
                      onChange={(e) => {
                        setContactsSearch(e.target.value);
                        setContactsPage(1);
                      }}
                      placeholder="Search contacts..."
                      className="w-full h-9 pl-9 pr-3 rounded-lg bg-[#0D0D0D] border border-[#262626] text-xs text-white placeholder-[#737373] focus:outline-none focus:border-[#333] transition-colors"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 overflow-x-auto sm:overflow-visible py-1 sm:py-0">
                  {!isSystemList && (
                    <>
                      {/* CSV Import */}
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-1 h-9 px-3 rounded-lg border border-[#262626] text-xs text-[#A3A3A3] hover:text-white hover:border-[#333] transition-colors flex-shrink-0"
                      >
                        <FiUpload size={12} />
                        Import
                      </button>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];

                          if (file) {
                            const parseResult = await parseMailContactsCsvFile(file);

                            if (parseResult.errors.length > 0) {
                              toast.error(parseResult.errors[0]);
                              e.target.value = '';
                              return;
                            }

                            if (parseResult.summary.validRows === 0) {
                              toast.error('No valid contacts found in CSV.');
                              e.target.value = '';
                              return;
                            }

                            if (parseResult.summary.skippedRows > 0) {
                              toast.warn(
                                `Importing ${parseResult.summary.validRows} contacts and skipping ${parseResult.summary.skippedRows} invalid rows.`,
                              );
                            }

                            csvImportMutation.mutate({ listUuid, file });
                          }

                          e.target.value = '';
                        }}
                        accept=".csv"
                        className="hidden"
                      />
                      <a
                        href="/mail_contacts_sample.csv"
                        download
                        className="flex items-center h-9 px-3 rounded-lg border border-[#262626] text-xs text-[#A3A3A3] hover:text-white hover:border-[#333] transition-colors flex-shrink-0"
                      >
                        Sample CSV
                      </a>
                    </>
                  )}

                  {/* Export CSV */}
                  <button
                    onClick={handleExportCsv}
                    disabled={
                      csvExportMutation.isPending || (mailingList?.total_contacts ?? 0) === 0
                    }
                    className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#262626] text-xs text-[#A3A3A3] hover:text-white hover:border-[#333] transition-colors flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {csvExportMutation.isPending ? (
                      <FiLoader size={12} className="animate-spin" />
                    ) : (
                      <FiDownload size={12} />
                    )}
                    Export
                  </button>

                  {/* Add Contact */}
                  {!isSystemList && (
                    <button
                      onClick={() => setShowAddEmails(true)}
                      className="flex items-center gap-1.5 h-9 px-4 rounded-full bg-white text-black text-xs font-semibold hover:bg-gray-100 transition-colors flex-shrink-0"
                    >
                      <FiPlus size={12} />
                      Add
                    </button>
                  )}
                </div>
              </div>

              {/* Contacts list */}
              <div className="flex-1 overflow-y-auto px-3 md:px-6 pb-3 md:pb-4">
                {contacts.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-xs text-[#737373]">
                      {contactsSearch
                        ? 'No contacts matching your search'
                        : 'No contacts in this list'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {contacts.map((contact: MailingContact) => (
                      <ContactRow
                        key={contact.uuid}
                        contact={contact}
                        onEdit={() => setEditContact(contact)}
                        onDelete={() => setDeleteContactTarget(contact)}
                        readOnly={isSystemList}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Pagination */}
              {contactsCount > 15 && (
                <div className="flex items-center justify-center gap-4 py-3 border-t border-[#262626] flex-shrink-0">
                  <button
                    disabled={!contactsPrev}
                    onClick={() => setContactsPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 text-xs text-[#A3A3A3] hover:text-white disabled:opacity-30 transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-[#737373]">Page {contactsPage}</span>
                  <button
                    disabled={!contactsNext}
                    onClick={() => setContactsPage((p) => p + 1)}
                    className="px-3 py-1.5 text-xs text-[#A3A3A3] hover:text-white disabled:opacity-30 transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Emails Modal */}
      {showAddEmails && (
        <AddEmailsModal
          onClose={() => setShowAddEmails(false)}
          onAdd={(emails) => {
            addContactsMutation.mutate(
              { contacts: emails.map((e) => ({ email: e })) },
              {
                onSuccess: () => setShowAddEmails(false),
              },
            );
          }}
          listName={mailingList?.name ?? 'List'}
        />
      )}

      {/* Edit Contact Modal */}
      {editContact && (
        <EditContactModal
          contact={{
            email: editContact.email,
            name: editContact.name,
          }}
          isSaving={updateContactMutation.isPending}
          onClose={() => setEditContact(null)}
          onSave={(updated) => {
            updateContactMutation.mutate(
              {
                listUuid,
                contactUuid: editContact.uuid,
                payload: { email: updated.email, name: updated.name },
              },
              {
                onSuccess: () => setEditContact(null),
              },
            );
          }}
        />
      )}

      {/* Delete Contact Modal */}
      {deleteContact && (
        <DeleteConfirmationModal
          title="Remove Contact"
          message={`Are you sure you want to remove ${deleteContact.email} from this list?`}
          onClose={() => setDeleteContactTarget(null)}
          onConfirm={() => {
            deleteContactMutation.mutate(
              { listUuid, contactUuid: deleteContact.uuid },
              {
                onSuccess: () => setDeleteContactTarget(null),
              },
            );
          }}
        />
      )}
    </>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatItem({
  value,
  label,
  variant,
}: {
  value: number;
  label: string;
  variant?: 'green' | 'red';
}) {
  const config = {
    green: {
      dot: 'bg-[#00D26A]',
      value: 'text-[#00D26A]',
      bg: 'bg-[#00D26A]/[0.07]',
      border: 'border-[#00D26A]/20',
      shadow: 'shadow-[0_0_12px_rgba(0,210,106,0.08)]',
    },
    red: {
      dot: 'bg-[#F87171]',
      value: 'text-[#F87171]',
      bg: 'bg-[#F87171]/[0.07]',
      border: 'border-[#F87171]/20',
      shadow: 'shadow-[0_0_12px_rgba(248,113,113,0.08)]',
    },
    default: {
      dot: 'bg-white/40',
      value: 'text-white',
      bg: 'bg-white/[0.04]',
      border: 'border-white/10',
      shadow: '',
    },
  };

  const c = variant ? config[variant] : config.default;

  return (
    <div
      className={`flex items-center gap-2.5 px-4 py-2 rounded-full border ${c.bg} ${c.border} ${c.shadow}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`} />
      <span className={`text-sm font-bold tabular-nums ${c.value}`}>{value.toLocaleString()}</span>
      <span className="text-xs text-[#606060] font-medium">{label}</span>
    </div>
  );
}

function TabButton({
  label,
  count,
  isActive,
  onClick,
}: {
  label: string;
  count?: number;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
        isActive
          ? 'text-white border-white'
          : 'text-[#A3A3A3] border-transparent hover:text-gray-300'
      }`}
    >
      {label}
      {count !== undefined && (
        <span className="text-xs bg-[#262626] px-1.5 py-0.5 rounded-full">{count}</span>
      )}
    </button>
  );
}

function BroadcastStatusPill({ status }: { status: BroadcastEmail['status'] }) {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    draft: { bg: 'bg-gray-500/10', text: 'text-gray-400', label: 'Draft' },
    scheduled: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Scheduled' },
    sending: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', label: 'Sending' },
    sent: { bg: 'bg-green-500/10', text: 'text-green-400', label: 'Sent' },
  };
  const c = config[status] ?? config.draft;
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${c.bg} ${c.text}`}
    >
      {c.label}
    </span>
  );
}

function MiniStat({ value, label, variant }: { value: number; label: string; variant?: 'red' }) {
  return (
    <div className="flex items-center gap-1">
      <span
        className={`text-xs font-semibold ${variant === 'red' ? 'text-red-400' : 'text-white'}`}
      >
        {value.toLocaleString()}
      </span>
      <span className="text-[10px] text-[#737373]">{label}</span>
    </div>
  );
}

function SourceBadge({ source }: { source: MailingContact['source'] }) {
  const config: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
    ticket: {
      bg: 'bg-purple-500/10',
      text: 'text-purple-400',
      icon: <FiCheckCircle size={10} />,
    },
    import: {
      bg: 'bg-blue-500/10',
      text: 'text-blue-400',
      icon: <FiFileText size={10} />,
    },
    manual: {
      bg: 'bg-gray-500/10',
      text: 'text-gray-400',
      icon: <FiPlus size={10} />,
    },
    subscribe: {
      bg: 'bg-emerald-500/10',
      text: 'text-emerald-400',
      icon: <FiUsers size={10} />,
    },
  };
  const c = config[source] ?? config.manual;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${c.bg} ${c.text}`}
    >
      {c.icon}
      {source}
    </span>
  );
}

function ContactRow({
  contact,
  onEdit,
  onDelete,
  readOnly,
}: {
  contact: MailingContact;
  onEdit: () => void;
  onDelete: () => void;
  readOnly?: boolean;
}) {
  const initials = (contact.name || contact.email.substring(0, 2)).substring(0, 2).toUpperCase();

  return (
    <div
      className={`flex items-center justify-between p-2.5 md:p-3 rounded-lg md:rounded-xl border transition-colors group ${
        contact.is_unsubscribed
          ? 'border-red-900/30 bg-red-950/10 opacity-60'
          : 'border-[#262626] hover:border-[#333]'
      }`}
    >
      <div className="flex items-center gap-2.5 md:gap-3 min-w-0 flex-1">
        <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-[#1c1c1e] flex items-center justify-center text-[10px] font-bold text-white uppercase flex-shrink-0 border border-[#262626]">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          {contact.name && (
            <p className="text-xs font-medium text-white truncate">{contact.name}</p>
          )}
          <div className="flex items-center gap-1.5">
            <p className="text-[10px] md:text-[11px] text-[#A3A3A3] truncate font-mono">
              {contact.email}
            </p>
            {contact.is_unsubscribed && (
              <span className="text-[9px] font-medium text-red-400/80 bg-red-500/10 px-1.5 py-0.5 rounded-full uppercase tracking-wider flex-shrink-0">
                Unsubscribed
              </span>
            )}
          </div>
        </div>
        <SourceBadge source={contact.source} />
      </div>
      {!readOnly && (
        <div className="flex items-center gap-0.5 md:gap-1 flex-shrink-0 ml-2 md:ml-3 opacity-100 md:opacity-50 md:group-hover:opacity-100 transition-opacity">
          <button
            onClick={onEdit}
            className="p-1.5 rounded-lg text-[#737373] hover:text-white hover:bg-white/5 transition-colors"
            title="Edit contact"
          >
            <FiEdit2 size={12} />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg text-[#737373] hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="Remove contact"
          >
            <FiTrash2 size={12} />
          </button>
        </div>
      )}
    </div>
  );
}
